import React, { useMemo, useState } from 'react'
import { SettingSummaryRow, Well } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../../_utils/classnames'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { getOnScreenSummary, getTriggerSummary } from '../summaries'
import { BehaviorSummaryRowKey, Readiness } from '../readiness'
import { AppMessageConditionsBuilder } from './app-message-conditions-builder'
import { AppMessageBehaviorBuilder } from './app-message-behavior-builder'
import { AppMessageScheduleBuilder, getScheduleSummary } from './app-message-schedule-builder'

type AppMessageBehaviorSummaryProps = {
    readiness: Readiness
}

/**
 * The fourth step, as three sentences rather than three cards of controls.
 *
 * Rows open independently, so trigger and schedule can be compared side by side
 * without either closing the other. A row whose readiness check is failing opens
 * itself, so a blocked submit never leaves the reader hunting for the control
 * that would unblock it — unless they have explicitly closed it again.
 */
export const AppMessageBehaviorSummary = ({ readiness }: AppMessageBehaviorSummaryProps) => {
    const [context] = useAppMessageBuilderContext()
    const variant = context.variants[context.selectedVariantIdx]
    const schedule = variant.schedules[0]

    const [openRows, setOpenRows] = useState<BehaviorSummaryRowKey[]>([])
    const [dismissedRows, setDismissedRows] = useState<BehaviorSummaryRowKey[]>([])

    const failingRows = useMemo(
        () =>
            readiness.checks
                .filter((check) => !check.satisfied && !!check.revealRow)
                .map((check) => check.revealRow as BehaviorSummaryRowKey),
        [readiness.checks],
    )

    const rows: { key: BehaviorSummaryRowKey; label: string; summary: string; content: React.ReactNode }[] = [
        {
            key: 'trigger',
            label: 'Trigger',
            summary: getTriggerSummary(schedule.conditions),
            content: <AppMessageConditionsBuilder />,
        },
        {
            key: 'onScreen',
            label: 'On screen',
            summary: getOnScreenSummary(schedule.behavior, variant.priority),
            content: <AppMessageBehaviorBuilder />,
        },
        {
            key: 'schedule',
            label: 'Schedule',
            summary: getScheduleSummary(schedule),
            content: <AppMessageScheduleBuilder />,
        },
    ]

    return (
        <Well
            step={4}
            title="When and How It Behaves"
            className={getClassNames('app-message-behavior-summary')}
            footerMode="hidden"
            padding="none"
        >
            {rows.map((row) => {
                const isFailing = failingRows.includes(row.key)
                const isRevealed = isFailing && !dismissedRows.includes(row.key)

                return (
                    <SettingSummaryRow
                        key={row.key}
                        label={row.label}
                        summary={row.summary}
                        // A chevron rather than Change/Done: nothing here is
                        // saved on its own, so a Done button would suggest the
                        // rest of the form is holding unsaved work.
                        toggleAppearance="disclosure"
                        status={isFailing ? 'attention' : 'default'}
                        disabled={context.disabled}
                        expanded={openRows.includes(row.key) || isRevealed}
                        onExpandedChange={(expanded) => {
                            setOpenRows((current) =>
                                expanded
                                    ? current.includes(row.key)
                                        ? current
                                        : [...current, row.key]
                                    : current.filter((key) => key !== row.key),
                            )

                            // A row forced open by a failing check has to be
                            // closeable, or Done would do nothing.
                            setDismissedRows((current) =>
                                expanded
                                    ? current.filter((key) => key !== row.key)
                                    : current.includes(row.key)
                                      ? current
                                      : [...current, row.key],
                            )
                        }}
                    >
                        {row.content}
                    </SettingSummaryRow>
                )
            })}
        </Well>
    )
}
